import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import './GlobalSearch.css';

const TYPE_LABELS = { project: 'Projets', client: 'Clients', employee: 'Salariés', vehicle: 'Véhicules', team: 'Équipes' };
const TYPE_ICONS = { project: '🧾', client: '🏢', employee: '👤', vehicle: '🚐', team: '🦺' };

export default function GlobalSearch({ setToolRequest }) {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const [query,setQuery] = useState('');
  const [results,setResults] = useState([]);
  const [loading,setLoading] = useState(false);
  const [open,setOpen] = useState(false);
  const [error,setError] = useState('');

  useEffect(() => {
    const close = event => { if (!rootRef.current?.contains(event.target)) setOpen(false); };
    document.addEventListener('mousedown',close);
    return () => document.removeEventListener('mousedown',close);
  },[]);

  useEffect(() => {
    const needle = query.trim();
    if (needle.length < 2) { setResults([]); setLoading(false); setError(''); return; }
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setLoading(true); setError('');
      try {
        const response = await fetch(`${config.apiBase}/search.php?q=${encodeURIComponent(needle)}`, {
          cache:'no-store', signal:controller.signal,
          headers:{Authorization:`Bearer ${localStorage.getItem(config.tokenKey) || ''}`},
        });
        const data = await response.json().catch(()=>({}));
        if (!response.ok) throw new Error(data.error || data.message || 'Recherche indisponible.');
        setResults(data.results || []); setOpen(true);
      } catch (requestError) {
        if (requestError.name !== 'AbortError') setError(requestError.message);
      } finally { if (!controller.signal.aborted) setLoading(false); }
    },250);
    return () => { window.clearTimeout(timer); controller.abort(); };
  },[query]);

  const grouped = Object.entries(TYPE_LABELS).map(([type,label]) => ({ type,label,items:results.filter(item=>item.type===type) })).filter(group=>group.items.length);
  const openResult = result => {
    if (result.app === 'kanban') {
      setToolRequest({ app:'kanban', sourceProject:result.source_key || '', nonce:Date.now() });
      navigate('/kanban');
    } else {
      navigate(`/${result.app}?search=${encodeURIComponent(result.title)}`);
    }
    setQuery(''); setResults([]); setOpen(false);
  };

  return <header className="global-search-bar">
    <div className="global-search" ref={rootRef}>
      <span className="global-search-icon">⌕</span>
      <input value={query} onFocus={()=>setOpen(query.trim().length>=2)} onChange={event=>setQuery(event.target.value)} onKeyDown={event=>{if(event.key==='Escape'){setOpen(false);event.currentTarget.blur();}}} placeholder="Rechercher un projet, client, commune, salarié ou véhicule…" aria-label="Recherche globale" />
      {loading&&<span className="global-search-loading" aria-label="Recherche en cours" />}
      {query&&<button className="global-search-clear" aria-label="Effacer la recherche" onClick={()=>{setQuery('');setResults([]);}}>×</button>}
      {open&&<section className="global-search-results" aria-live="polite">
        {error&&<p className="global-search-message error">{error}</p>}
        {!error&&!loading&&!results.length&&<p className="global-search-message">Aucun résultat pour « {query.trim()} ».</p>}
        {grouped.map(group=><div className="global-result-group" key={group.type}><h2>{group.label}</h2>{group.items.map(item=><button key={`${item.type}-${item.id}`} onClick={()=>openResult(item)}><i>{TYPE_ICONS[item.type]}</i><span><strong>{item.title}</strong><small>{item.subtitle}</small></span><em>{item.meta}</em><b>→</b></button>)}</div>)}
      </section>}
    </div>
  </header>;
}
